import React from 'react';
import { FormControlLabel, Grid, Input, InputAdornment } from '@material-ui/core';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { withStyles } from '@material-ui/core/styles';
import { withCanvas } from 'redhotmagma-graphics-editor/CanvasInterface/CanvasProvider';
import { mmsToPixels, pixelsToMms } from '../Utils/LengthUnitConverter';

// eslint-disable-next-line no-undef
type Dimension = 'width' | 'height' | 'rotation';

const getSelectedObjectDimensions = (canvas) => {
  const { selectedObject } = canvas;
  if (!selectedObject) {
    return { width: 0, height: 0 };
  }

  const size = selectedObject.getScaledSize();

  return {
    width: pixelsToMms(size.width || 0, canvas.size.dpi),
    height: pixelsToMms(size.height || 0, canvas.size.dpi),
  };
};

const setSizeByDimension = (canvas, dimension: Dimension, valueMm: number) => {
  const { selectedObject } = canvas;
  if (!selectedObject) return;

  const currentSize = selectedObject.getScaledSize();
  const dpi = canvas.size.dpi;
  const targetPx = mmsToPixels(valueMm, dpi);

  let scaleFactor: number;

  if (dimension === 'width') {
    scaleFactor = targetPx / currentSize.width;
  } else {
    scaleFactor = targetPx / currentSize.height;
  }

  selectedObject.scaling = selectedObject.scaling * scaleFactor;
};

const setSelectedObjectRotation = (canvas, degrees: number) => {
  const { selectedObject } = canvas;
  if (!selectedObject) return;

  selectedObject.rotation = degrees;
};

const getSelectedObjectRotation = (canvas): number => {
  const { selectedObject } = canvas;
  if (!selectedObject) return 0;

  return parseInt(selectedObject.rotation);
};

const StyledFormControlLabel = withStyles({
  labelPlacementStart: {
    marginLeft: 0,
    marginRight: 0,
    marginTop: 12,
    width: '100%',
    '& > .MuiTypography-root': {
      marginRight: 8,
    },
  },
})(FormControlLabel);

const ImageSizeInput = ({ canvas, dimension }: { canvas: object; dimension: Dimension }) => {
  const handleOnChange = (evt) => {
    const value = Math.max(1, evt.target.value);
    setSizeByDimension(canvas, dimension, value);
  };
  return dimension === 'rotation' ? (
    <StyledFormControlLabel
      label={`${t(dimension)}:`}
      labelPlacement="start"
      control={
        <Input
          type="number"
          style={{ width: '100%' }}
          endAdornment={<InputAdornment position="end">deg</InputAdornment>}
          value={getSelectedObjectRotation(canvas)}
          inputProps={{ min: 0, max: 360 }}
          onChange={(e) => {
            const val = parseInt(e.target.value);
            if (isNaN(val)) {
              setSelectedObjectRotation(canvas, 0);
            } else {
              setSelectedObjectRotation(canvas, Math.min(360, Math.max(0, val)));
            }
          }}
        />
      }
    />
  ) : (
    <StyledFormControlLabel
      label={`${t(dimension)}:`}
      labelPlacement="start"
      control={
        <Input
          style={{ width: '100%' }}
          type="number"
          endAdornment={<InputAdornment position="end">mm</InputAdornment>}
          value={Math.round(getSelectedObjectDimensions(canvas)[dimension] || 0)}
          onChange={handleOnChange}
        />
      }
    />
  );
};

const ObjectSizeControls = ({ canvas }: { canvas: object }) => (
  <>
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <ImageSizeInput canvas={canvas} dimension={'width'} />
      </Grid>
      <Grid item xs={12} md={6}>
        <ImageSizeInput canvas={canvas} dimension={'height'} />
      </Grid>
    </Grid>
    <Grid container>
      <Grid item xs={12} md={6}>
        <ImageSizeInput canvas={canvas} dimension={'rotation'} />
      </Grid>
    </Grid>
  </>
);

export default withCanvas(ObjectSizeControls);
